'use client';

import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { ValueOf } from 'type-fest';

import { AerialState } from 'lib/generated/proto/aerial_interfaces/msg/AerialState';
import { Location } from 'lib/generated/proto/robot_interfaces/msg/Location';
import { RobotState } from 'lib/generated/proto/robot_interfaces/msg/RobotState';
import {
  Pose,
  PoseStamped,
} from 'lib/generated/proto/roboverse_platform_protos/statekeeping/Common/Geometry.ts';
import { Session } from 'lib/generated/proto/roboverse_platform_protos/statekeeping/Config/Session';
import { DataUpload } from 'lib/generated/proto/roboverse_platform_protos/statekeeping/Reported/DataUpload.ts';
import { DockOSD } from 'lib/generated/proto/roboverse_platform_protos/statekeeping/Reported/Drone/DJI/DockOSD.ts';
import {
  DroneOSD,
  DroneOSD_GimbalIndex,
  flightModeFromJSON,
  flightModeReasonFromJSON,
} from 'lib/generated/proto/roboverse_platform_protos/statekeeping/Reported/Drone/DJI/DroneOSD.ts';
import { Localization } from 'lib/generated/proto/roboverse_platform_protos/statekeeping/Reported/Localization.ts';
import { MissionStatus } from 'lib/generated/proto/roboverse_platform_protos/statekeeping/Reported/MissionStatus.ts';
import { BlkArc } from 'lib/generated/proto/roboverse_platform_protos/statekeeping/Reported/Payload/BlkArc.ts';
import { SpotCam } from 'lib/generated/proto/roboverse_platform_protos/statekeeping/Reported/Payload/SpotCam.ts';
import {
  SpotFaults,
  SpotFaults_SpotFault,
} from 'lib/generated/proto/roboverse_platform_protos/statekeeping/Reported/SpotFaults';
import {
  Status,
  Status_Battery,
  Status_EmergencyStopState,
  Status_GPSLocation,
} from 'lib/generated/proto/roboverse_platform_protos/statekeeping/Reported/Status.ts';
import { Streaming } from 'lib/generated/proto/roboverse_platform_protos/statekeeping/Reported/Streaming.ts';
import { PowerPolicy } from 'lib/generated/proto/roboverse_platform_protos/statekeeping/Reported/__Common/PowerPolicy';
import { BatteryState } from 'lib/generated/proto/sensor_msgs/msg/BatteryState';

import { logRed, logYellow } from 'utils/debugger';

import { DeviceName } from 'types/models';

import { fetchPostTwin, fetchTwin } from '../statekeeping';
import { HUB_URL } from './const';
import { SignalRHub, useSignalRHub } from './context/useSignalRHub';

export enum TwinHubEvents {
  GetData = 'GetData',
  SendData = 'SendData',
  SubscribeToUpdates = 'SubscribeToUpdates',
  UnsubscribeFromUpdates = 'UnsubscribeFromUpdates',
  ReceiveData = 'ReceiveData',
}

// for accessing the redis store (state keeping)
const TWIN_HUB_KEYS = {
  CONFIG_SESSION: (deviceName: DeviceName) => `${deviceName}:Config:Session` as const,
  PAYLOADS_BLK_ARC: (deviceName: DeviceName) => `${deviceName}:Reported:Payload:BlkArc` as const,
  PAYLOADS_SPOT_CAM: (deviceName: DeviceName) => `${deviceName}:Reported:Payload:SpotCam` as const,
  MISSION_STATUS: (deviceName: DeviceName) => `${deviceName}:Reported:MissionStatus` as const,
  DATA_UPLOAD: (deviceName: DeviceName) => `${deviceName}:Reported:DataUpload` as const,
  LOCALIZATION: (deviceName: DeviceName) => `${deviceName}:Reported:Localization` as const,
  STATUS: (deviceName: DeviceName) => `${deviceName}:Reported:Status` as const,
  STREAMING: (deviceName: DeviceName) => `${deviceName}:Reported:Streaming` as const,
  SPOT_FAULTS: (deviceName: DeviceName) => `${deviceName}:Reported:SpotFaults` as const,
  DRONE_OSD: (deviceName: DeviceName) => `${deviceName}:Reported:Drone:DJI:DroneOSD` as const,
  DOCK_OSD: (deviceName: DeviceName) => `${deviceName}:Reported:Drone:DJI:DockOSD` as const,
  ROBOT_STATE: (deviceName: DeviceName) => `${deviceName}:Reported:State` as const,
  ROBOT_LOCATION: (deviceName: DeviceName) => `${deviceName}:Reported:Location` as const,
  AERIAL_STATE: (deviceName: DeviceName) => `${deviceName}:Reported:State:Aerial` as const,
} as const;

export type KeyPathParser = {
  keyPath: (deviceName: DeviceName) => KeyPath;
  parser: (data: any) => unknown;
};

// todo rewrite props to map to the real redis
// todo write type for path that it is mapping to JSONPath for parser return type
//  Maybe the react query key helper lib knows how to handle this
/**
 * - Always add a function for "parser" and define fallback value for data
 * - Name of the attributes need to follow the "key" in redis and "path" the naming in the json/proto
 */
export const KeyPathParser = {
  Status: {
    keyPathParser: {
      keyPath: (deviceName: DeviceName) =>
        ({
          key: TWIN_HUB_KEYS.STATUS(deviceName),
        } satisfies KeyPath),
      parser: (data: any) => Status.fromJSON(data || {}),
    } satisfies KeyPathParser,
    emergencyStopState: {
      keyPath: (deviceName: DeviceName) =>
        ({
          key: TWIN_HUB_KEYS.STATUS(deviceName),
          path: '$.emergencyStopState',
        } satisfies KeyPath),
      parser: (data: any) => Status_EmergencyStopState.fromJSON(data || {}),
    },
    operationMode: {
      keyPath: (deviceName: DeviceName) =>
        ({
          key: TWIN_HUB_KEYS.STATUS(deviceName),
          path: '$.operationMode',
        } satisfies KeyPath),
      parser: (data: any) => Status.fromJSON({ operationMode: data }).operationMode,
    },
    battery: {
      keyPath: (deviceName: DeviceName) =>
        ({
          key: TWIN_HUB_KEYS.STATUS(deviceName),
          path: '$.battery',
        } satisfies KeyPath),
      parser: (data: any) => Status_Battery.fromJSON(data || {}),
    },
    hasArm: {
      keyPath: (deviceName: DeviceName) =>
        ({
          key: TWIN_HUB_KEYS.STATUS(deviceName),
          path: '$.hasArm',
        } satisfies KeyPath),
      parser: (data: any) => !!data as boolean,
    },
    location: {
      keyPath: (deviceName: DeviceName) =>
        ({
          key: TWIN_HUB_KEYS.STATUS(deviceName),
          path: '$.location',
        } satisfies KeyPath),
      parser: (data: any) => Status_GPSLocation.fromJSON(data || {}),
    },
    powerPolicy: {
      keyPath: (deviceName: DeviceName) =>
        ({
          key: TWIN_HUB_KEYS.STATUS(deviceName),
          path: '$.power_policy',
        } satisfies KeyPath),
      parser: (data: any) => PowerPolicy.fromJSON(data || {}),
    },
    shouldRobotRestart: {
      keyPath: (deviceName: DeviceName) =>
        ({
          key: TWIN_HUB_KEYS.STATUS(deviceName),
          path: '$.shouldRobotRestart',
        } satisfies KeyPath),
      parser: (data: any) => !!data,
    },
  },
  CONFIG_SESSION: {
    keyPath: (deviceName: DeviceName) =>
      ({
        key: TWIN_HUB_KEYS.CONFIG_SESSION(deviceName),
        path: '$',
      } satisfies KeyPath),
    parser: (data: any) => Session.fromJSON(data || {}),
    uniqueSessionId: {
      keyPath: (deviceName: DeviceName) =>
        ({
          key: TWIN_HUB_KEYS.CONFIG_SESSION(deviceName),
          path: '$.uniqueSessionId',
        } satisfies KeyPath),
      parser: (data: any) => data as string,
    },
  },
  Localization: {
    keyPath: (deviceName: DeviceName) =>
      ({
        key: TWIN_HUB_KEYS.LOCALIZATION(deviceName),
      } satisfies KeyPath),
    parser: (data: any) => Localization.fromJSON(data || {}),
    robotPose: {
      keyPath: (deviceName: DeviceName) =>
        ({
          key: TWIN_HUB_KEYS.LOCALIZATION(deviceName),
          path: '$.robotPose',
        } satisfies KeyPath),
      parser: (data: any) => PoseStamped.fromJSON(data || {}),
      pose: {
        keyPath: (deviceName: DeviceName) =>
          ({
            key: TWIN_HUB_KEYS.LOCALIZATION(deviceName),
            path: '$.robotPose.pose',
          } satisfies KeyPath),
        parser: (data: any) => Pose.fromJSON(data || {}),
      },
    },
    isMapRecording: {
      keyPath: (deviceName: DeviceName) =>
        ({
          key: TWIN_HUB_KEYS.LOCALIZATION(deviceName),
          path: '$.isMapRecording',
        } satisfies KeyPath),
      parser: (data: any) => !!data,
    },
  },
  PayloadsBlkArc: {
    keyPath: (deviceName: DeviceName) =>
      ({
        key: TWIN_HUB_KEYS.PAYLOADS_BLK_ARC(deviceName),
      } satisfies KeyPath),
    parser: (data: any) => BlkArc.fromJSON(data || {}),
  },
  PayloadsSpotCam: {
    keyPath: (deviceName: DeviceName) =>
      ({
        key: TWIN_HUB_KEYS.PAYLOADS_SPOT_CAM(deviceName),
        path: '$',
      } satisfies KeyPath),
    parser: (data: any) => SpotCam.fromJSON(data || {}),
  },
  Streaming: {
    keyPath: (deviceName: DeviceName) =>
      ({
        key: TWIN_HUB_KEYS.STREAMING(deviceName),
      } satisfies KeyPath),
    parser: (data: any) => Streaming.fromJSON(data || {}),
  },
  SpotFaults: {
    keyPath: (deviceName: DeviceName) =>
      ({
        key: TWIN_HUB_KEYS.SPOT_FAULTS(deviceName),
      } satisfies KeyPath),
    parser: (data: any) => SpotFaults.fromJSON(data || {}),
    activeFaults: {
      keyPath: (deviceName: DeviceName) =>
        ({
          key: TWIN_HUB_KEYS.SPOT_FAULTS(deviceName),
          path: '$',
        } satisfies KeyPath),
      parser: (data: any[]) => {
        // no data result in empty string!
        if (!data?.length) return '';
        return data?.map((d) => SpotFaults_SpotFault.fromJSON(d)) || [];
      },
    },
  },
  DataUpload: {
    keyPath: (deviceName: DeviceName) =>
      ({
        key: TWIN_HUB_KEYS.DATA_UPLOAD(deviceName),
      } satisfies KeyPath),
    parser: (data: any) => DataUpload.fromJSON(data || {}),
  },
  DroneOSD: {
    keyPath: (deviceName: DeviceName) =>
      ({
        key: TWIN_HUB_KEYS.DRONE_OSD(deviceName),
      } satisfies KeyPath),
    parser: (data: any) => DroneOSD.fromJSON(data || {}),
    modeCode: {
      keyPath: (deviceName: DeviceName) =>
        ({
          key: TWIN_HUB_KEYS.DRONE_OSD(deviceName),
          path: '$.modeCode',
        } satisfies KeyPath),
      parser: (data: unknown) => flightModeFromJSON(data),
    },
    modeCodeReason: {
      keyPath: (deviceName: DeviceName) =>
        ({
          key: TWIN_HUB_KEYS.DRONE_OSD(deviceName),
          path: '$.modeCodeReason',
        } satisfies KeyPath),
      // todo for whatever reason in the protobuff define this as enum but in pranavs redis we have the msg as string
      parser: (data: any) => (data?.length > 3 ? (data as string) : flightModeReasonFromJSON(data)),
    },
    gimbalIndex: {
      keyPath: (deviceName: DeviceName) =>
        ({
          key: TWIN_HUB_KEYS.DRONE_OSD(deviceName),
          path: '$.gimbalIndex',
        } satisfies KeyPath),
      parser: (data: any) => DroneOSD_GimbalIndex.fromJSON(data || {}),
      payloadIndex: {
        keyPath: (deviceName: DeviceName) =>
          ({
            key: TWIN_HUB_KEYS.DRONE_OSD(deviceName),
            path: '$.gimbalIndex.payloadIndex',
          } satisfies KeyPath),
        parser: (data: any) => data as string | '',
      },
    },
  },
  DockOSD: {
    keyPath: (deviceName: DeviceName) =>
      ({
        key: TWIN_HUB_KEYS.DOCK_OSD(deviceName),
      } satisfies KeyPath),
    parser: (data: any) => DockOSD.fromJSON(data || {}),
  },
  RobotState: {
    keyPath: (deviceName: DeviceName) =>
      ({
        key: TWIN_HUB_KEYS.ROBOT_STATE(deviceName),
      } satisfies KeyPath),
    parser: (data: any) => RobotState.fromJSON(data ?? {}),
    isArmed: {
      keyPath: (deviceName: DeviceName) =>
        ({
          key: TWIN_HUB_KEYS.ROBOT_STATE(deviceName),
          path: '$.operation_mode.armed',
        } satisfies KeyPath),
      parser: (data: any) => !!data as boolean,
    },
    battery: {
      keyPath: (deviceName: DeviceName) =>
        ({
          key: TWIN_HUB_KEYS.ROBOT_STATE(deviceName),
          path: '$.battery',
        } satisfies KeyPath),
      parser: (data: any) => BatteryState.fromJSON(data ?? {}),
    },
  },
  RobotLocation: {
    keyPath: (deviceName: DeviceName) =>
      ({
        key: TWIN_HUB_KEYS.ROBOT_LOCATION(deviceName),
      } satisfies KeyPath),
    parser: (data: any) => Location.fromJSON(data ?? {}),
  },
  AerialState: {
    keyPath: (deviceName: DeviceName) =>
      ({
        key: TWIN_HUB_KEYS.AERIAL_STATE(deviceName),
      } satisfies KeyPath),
    parser: (data: any) => AerialState.fromJSON(data || {}),
  },
  MissionStatus: {
    keyPath: (deviceName: DeviceName) =>
      ({
        key: TWIN_HUB_KEYS.MISSION_STATUS(deviceName),
      } satisfies KeyPath),
    parser: (data: any) => MissionStatus.fromJSON(data || {}),
    navigationProgress: {
      currentRobotLocation: {
        keyPath: (deviceName: DeviceName) =>
          ({
            key: TWIN_HUB_KEYS.MISSION_STATUS(deviceName),
            path: '$.navigationProgress.currentRobotLocation',
          } satisfies KeyPath),
        parser: (data: any) => {
          return Pose.fromJSON(data || {});
        },
      },
    },
  },
};

export type Keys = ReturnType<ValueOf<typeof TWIN_HUB_KEYS>>;

export type KeyPath = {
  key: Keys;
  // the path should start with '$' and then the path to the data
  path?: string;
};

export type KeyPathValue = KeyPath & { value: Object };

class TwinHubClient {
  private readonly hub: SignalRHub;

  constructor(hub: SignalRHub) {
    this.hub = hub;
  }

  async getData(keyPath: KeyPath) {
    return this.hub.invoke(TwinHubEvents.GetData, keyPath).catch((error) => {
      logRed('Error getting data', error);
    });
  }

  async sendData(keyPathValue: KeyPathValue) {
    return this.hub.invoke(TwinHubEvents.SendData, keyPathValue).catch((error) => {
      logRed('Error sending data', error);
    });
  }

  // the callback reference will be saved for unsubscribing later
  subscribeToUpdates(keyPath: KeyPath, callback: (data: any) => void) {
    // tell the cloud that we want to receive updates for this key
    this.hub.invoke(TwinHubEvents.SubscribeToUpdates, { keyPaths: [keyPath] });

    // create a callback that only gets trigger if the key is the same as the one we are subscribing to
    const _callback = (receiveKey: string, receivePath: string, data: any) => {
      // every incoming data will trigger every callback we have
      if (receiveKey !== keyPath.key) return;
      if (keyPath.path && receivePath !== keyPath.path) return;
      // logYellow('TwinHub:callback:data', receiveKey, receivePath, data);
      callback(data);
    };

    // subscribe to the event that will be triggered when the data for the key is updated
    this.hub.subscribe(TwinHubEvents.ReceiveData, _callback);

    return () => {
      // remove the callback from the event
      this.hub.unsubscribe(TwinHubEvents.ReceiveData, _callback);
      // the server won't send updates for this key anymore
      this.hub.invoke(TwinHubEvents.UnsubscribeFromUpdates, { keyPaths: [keyPath] });
    };
  }
}

/**
 * Hook to use for the Teleportation hub

 * We are assuming that each user of this function will have his own key - it is not able to have the same key twice
 */
export function useTwinHub() {
  const hub = useSignalRHub(HUB_URL.TWIN);

  const TwinHubClientClass = useMemo(() => new TwinHubClient(hub), []);

  return { TwinHubClient: TwinHubClientClass, ...hub };
}

/**
 * Request data via the twin hub and a use query to store
 *
 */
export function useTwinQuery<T>(keyOPath: KeyPath, parser: (obj: any) => T) {
  const { TwinHubClient } = useTwinHub();

  const queryKey = [keyOPath];

  async function queryFn() {
    const data = await TwinHubClient.getData(keyOPath);
    logYellow('useTwinQuery:queryFn:data', keyOPath, data);
    // when no data was reported init a default object
    // if (!data) return parser({}); todo not sure why we added empty object but with path there can be data=null.
    //  if this got older without error remove comments/todo
    return parser(data);
  }

  const { data } = useSuspenseQuery({
    queryKey,
    queryFn,
  });

  return { data };
}

/**
 * Extend {useTwinQuery} with subscription to updates
 *
 */
export function useTwinQuerySub<T>(keyPath: KeyPath, parser: (obj: any) => T) {
  const { TwinHubClient } = useTwinHub();

  const queryClient = useQueryClient();
  const queryKey = [keyPath];

  const { data } = useTwinQuery(keyPath, parser);

  function handleUpdate(data: unknown) {
    const temp = parser(data);
    updateState(temp);
  }

  function updateState(data: T) {
    queryClient.setQueryData(queryKey, data);
  }

  // note - this maybe looks strange but works fine
  useEffect(() => {
    return TwinHubClient.subscribeToUpdates(keyPath, handleUpdate);
  }, []);

  return { data, updateState };
}

/**
 * Fetch data via the http api for twin hub and store in query
 * Use this only when you don't have a valid signalR connection.
 */
export function useTwinFetcherQuery<T>(keyPath: KeyPath, parser: (obj: any) => T) {
  const queryKey = [keyPath];

  const { data } = useSuspenseQuery({
    queryKey,
    queryFn: () => fetchTwin(keyPath, parser),
  });

  return data;
}

/**
 * WIP - not sure if this is a good way
 * @param keyPath
 * @param parser
 */
export function useTwinPost<T>(keyPath: KeyPath, parser: (obj: any) => T) {
  function update(data: ReturnType<typeof parser>) {
    return fetchPostTwin(keyPath, data);
  }

  return { update };
}
